/**
 * Enhanced Vendor Profile Management Hook
 * 
 * Provides comprehensive vendor profile management including:
 * - Vendor profile CRUD operations
 * - Certifications management
 * - Capabilities tracking
 * - Profile completion status calculation
 * - Verification request handling
 * - Real-time updates via Supabase subscriptions
 * 
 * @module useVendorProfileEnhanced
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Enhanced vendor profile with complete business and contact information
 */
export interface VendorProfileEnhanced {
  id: string;
  user_id: string;
  organization_id?: string | null;
  business_name?: string | null;
  business_type?: string | null;
  founded_year?: number | null;
  employee_count_range?: string | null;
  annual_revenue_range?: string | null;
  primary_address?: any;
  service_areas: any;
  country?: string | null;
  state?: string | null;
  street_address?: string | null;
  city?: string | null;
  zip_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  business_description?: string | null;
  value_proposition?: string | null;
  company_story?: string | null;
  website_url?: string | null;
  linkedin_url?: string | null;
  custom_fields: any;
  specializations: any;
  service_categories: any;
  is_verified: boolean;
  verification_level: string;
  verified_at?: string | null;
  verified_by_admin_id?: string | null;
  completion_rate: number;
  average_rating: number;
  total_jobs_completed: number;
  on_time_delivery_rate: number;
  gallery_images?: any;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  job_title?: string | null;
  department?: string | null;
  timezone?: string | null;
  // Required business documents
  w9_document_url?: string | null;
  w9_uploaded_at?: string | null;
  business_registration_url?: string | null;
  business_registration_uploaded_at?: string | null;
  cgl_insurance_url?: string | null;
  cgl_insurance_uploaded_at?: string | null;
  cgl_insurance_expiry_date?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Vendor certification details including verification status
 */
export interface VendorCertification {
  id: string;
  vendor_profile_id: string;
  certification_type_id?: string | null;
  certification_name?: string | null;
  issuing_organization: string;
  certificate_number?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  document_url?: string | null;
  verification_status: string;
  created_at: string;
  updated_at: string;
  certification_type?: {
    id: string;
    name: string;
    category_id: string;
    category?: {
      id: string;
      name: string;
    };
  };
}

/**
 * Vendor capability with business percentage tracking
 */
export interface VendorCapability {
  id: string;
  vendor_profile_id: string;
  capability_id: string;
  business_percentage: number;
  portfolio_urls: any;
  created_at: string;
  updated_at: string;
  capability?: {
    id: string;
    name: string;
  };
}

/**
 * Profile completion tracking across different sections
 */
export interface ProfileCompletion {
  id: string;
  profile_id: string;
  basic_info_complete: boolean;
  user_profile_complete: boolean;
  company_profile_complete: boolean;
  certifications_complete: boolean;
  capabilities_complete: boolean;
  gallery_complete: boolean;
  verification_complete: boolean;
  overall_percentage: number;
  created_at: string;
  updated_at: string;
}

/**
 * Main hook for vendor profile management
 * 
 * Provides complete vendor profile lifecycle management including:
 * - Profile data fetching and real-time updates
 * - CRUD operations for profile, certifications, and capabilities
 * - Automatic profile completion percentage calculation
 * - Verification request submission
 * - Toast notifications for all operations
 * 
 * The hook automatically:
 * - Subscribes to real-time profile updates
 * - Recalculates completion status when data changes
 * - Fetches related data (certifications, capabilities, verification requests)
 * 
 * @returns {Object} Vendor profile state and management functions
 * 
 * @example
 * ```tsx
 * function VendorProfilePage() {
 *   const {
 *     profile,
 *     certifications,
 *     capabilities,
 *     completion,
 *     loading,
 *     updateProfile,
 *     addCertification,
 *     requestVerification
 *   } = useVendorProfileEnhanced();
 *   
 *   if (loading) return <Spinner />;
 *   
 *   return (
 *     <div>
 *       <h1>{profile?.business_name}</h1>
 *       <ProfileCompletionBar percentage={completion?.overall_percentage} />
 *       <CertificationsList certifications={certifications} />
 *       <CapabilitiesList capabilities={capabilities} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useVendorProfileEnhanced = () => {
  const [profile, setProfile] = useState<VendorProfileEnhanced | null>(null);
  const [certifications, setCertifications] = useState<VendorCertification[]>([]);
  const [capabilities, setCapabilities] = useState<VendorCapability[]>([]);
  const [completion, setCompletion] = useState<ProfileCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<any | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);
  const { toast } = useToast();

  // Fetch vendor profile
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data: profileData, error: profileError } = await supabase
        .from('vendor_profiles_enhanced')
        .select(`
          *,
          organization:organizations!vendor_profiles_enhanced_organization_id_fkey(id, name)
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .order('updated_at', { ascending: false })
        .maybeSingle();

      if (profileError) throw profileError;
      
      // Enrich profile with organization name
      const enrichedProfile = profileData ? {
        ...profileData,
        organization_name: profileData.organization?.name || profileData.business_name
      } : null;
      
      setProfile(enrichedProfile as VendorProfileEnhanced);

      if (profileData) {
        // Fetch latest pending verification request
        const { data: reqData, error: reqError } = await supabase
          .from('verification_requests')
          .select('*')
          .eq('vendor_profile_id', profileData.id)
          .eq('status', 'pending')
          .order('requested_at', { ascending: false })
          .maybeSingle();

        if (reqError) {
          console.error('Failed to fetch verification request', reqError);
        }
        setPendingRequest(reqData || null);

        // Fetch certifications with joined type data
        const { data: certData, error: certError } = await supabase
          .from('vendor_certifications')
          .select(`
            *,
            certification_type:certification_types!vendor_certifications_certification_type_id_fkey(
              id,
              name,
              category_id,
              category:certification_categories!certification_types_category_id_fkey(id, name)
            )
          `)
          .eq('vendor_profile_id', profileData.id)
          .order('created_at', { ascending: false });

        if (certError) throw certError;
        setCertifications((certData || []) as VendorCertification[]);

        // Fetch capabilities with joined capability data
        const { data: capData, error: capError } = await supabase
          .from('vendor_capabilities')
          .select(`
            *,
            capability:capabilities(id, name)
          `)
          .eq('vendor_profile_id', profileData.id)
          .order('business_percentage', { ascending: false });

        if (capError) throw capError;
        setCapabilities((capData || []) as VendorCapability[]);


        // Fetch completion status
        const { data: compData, error: compError } = await supabase
          .from('vendor_profile_completion')
          .select('*')
          .eq('profile_id', profileData.id)
          .maybeSingle();

        if (compError) throw compError;
        setCompletion(compData);
      }
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch vendor profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Create vendor profile
  const createProfile = async (profileData: Partial<VendorProfileEnhanced>) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Fetch organization_id from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Failed to fetch user profile:', profileError);
      }

      // Filter out business_name from profileData
      const { business_name, ...cleanProfileData } = profileData as any;

      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .insert({
          user_id: user.data.user.id,
          organization_id: profile?.organization_id || cleanProfileData.organization_id,
          ...cleanProfileData,
        })
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as VendorProfileEnhanced);
      
      // Create completion tracking
      await supabase
        .from('vendor_profile_completion')
        .insert([{
          profile_id: data.id,
          basic_info_complete: false,
          certifications_complete: false,
          capabilities_complete: false,
          gallery_complete: false,
          verification_complete: false,
          overall_percentage: 0,
        }]);

      toast({
        title: "Success",
        description: "Vendor profile created successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to create vendor profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update vendor profile
  const updateProfile = async (updates: Partial<VendorProfileEnhanced>) => {
    try {
      if (!profile) throw new Error('No profile to update');

      setIsUpdating(true);
      isUpdatingRef.current = true;

      // Filter out business_name from updates
      const { business_name, ...cleanUpdates } = updates as any;
      // Remove undefined values to avoid overwriting with nulls
      const sanitizedUpdates = Object.fromEntries(
        Object.entries(cleanUpdates).filter(([_, v]) => v !== undefined)
      );

      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .update(sanitizedUpdates as any)
        .eq('id', profile.id)
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as VendorProfileEnhanced);
      await updateCompletionStatus(data.id);
      
      // Track profile update
      analytics.track({
        name: ANALYTICS_EVENTS.PROFILE_UPDATED,
        category: 'engagement',
        properties: {
          profile_type: 'vendor',
        },
      });

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      
      // Clear flag after a delay
      setTimeout(() => {
        setIsUpdating(false);
      }, 500);
      
      return data;
    } catch (err: any) {
      setIsUpdating(false);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Add certification
  const addCertification = async (certData: Partial<VendorCertification> & { document_url?: string }) => {
    try {
      if (!profile) throw new Error('No profile found');

      const { data, error } = await supabase
        .from('vendor_certifications')
        .insert({
          certification_name: certData.certification_name || '',
          issuing_organization: certData.issuing_organization || '',
          vendor_profile_id: profile.id,
          document_url: certData.document_url || null,
          ...certData,
        } as any)
        .select(`
          *,
          certification_type:certification_types!vendor_certifications_certification_type_id_fkey(
            id,
            name,
            category_id,
            category:certification_categories!certification_types_category_id_fkey(id, name)
          )
        `)
        .single();

      if (error) throw error;
      
      setCertifications(prev => [data, ...prev]);
      await updateCompletionStatus(profile.id);
      
      toast({
        title: "Success",
        description: "Certification added successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to add certification",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update certification
  const updateCertification = async (id: string, updates: Partial<VendorCertification>) => {
    try {
      const { data, error } = await supabase
        .from('vendor_certifications')
        .update(updates as any)
        .eq('id', id)
        .select(`
          *,
          certification_type:certification_types!vendor_certifications_certification_type_id_fkey(
            id,
            name,
            category_id,
            category:certification_categories!certification_types_category_id_fkey(id, name)
          )
        `)
        .single();

      if (error) throw error;
      
      setCertifications(prev => 
        prev.map(cert => cert.id === id ? { ...cert, ...data } : cert)
      );
      
      toast({
        title: "Success",
        description: "Certification updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update certification",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Add capability
  const addCapability = async (capData: { capability_id: string; business_percentage: number }) => {
    try {
      if (!profile) throw new Error('No profile found');

      const { data, error } = await supabase
        .from('vendor_capabilities')
        .insert({
          vendor_profile_id: profile.id,
          capability_id: capData.capability_id,
          business_percentage: capData.business_percentage,
        })
        .select(`
          *,
          capability:capabilities(id, name)
        `)
        .single();

      if (error) throw error;
      
      setCapabilities(prev => [...prev, data as VendorCapability]);
      await updateCompletionStatus(profile.id);
      
      toast({
        title: "Success",
        description: "Capability added successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to add capability",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update capability
  const updateCapability = async (id: string, updates: { business_percentage: number }) => {
    try {
      const { data, error } = await supabase
        .from('vendor_capabilities')
        .update({ business_percentage: updates.business_percentage })
        .eq('id', id)
        .select(`
          *,
          capability:capabilities(id, name)
        `)
        .single();

      if (error) throw error;
      
      setCapabilities(prev => 
        prev.map(cap => cap.id === id ? (data as VendorCapability) : cap)
      );
      
      toast({
        title: "Success",
        description: "Capability updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update capability",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update completion status
  const updateCompletionStatus = async (profileId: string) => {
    try {
      // Fetch fresh profile data
      const { data: profileData } = await supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('id', profileId)
        .single();

      if (!profileData) return;

      // User profile required fields
      const userFieldsComplete = !!(
        profileData.first_name && 
        profileData.last_name && 
        profileData.phone_number && 
        profileData.job_title
      );

      // Company profile required fields (business_description is optional)
      // Note: country is not included since the platform is US-only and the field is non-editable
      const companyFieldsComplete = !!(
        profileData.founded_year &&
        profileData.employee_count_range &&
        profileData.street_address &&
        profileData.city &&
        profileData.state &&
        profileData.zip_code &&
        profileData.website_url
      );

      const basicInfoComplete = userFieldsComplete && companyFieldsComplete;
      const certificationsComplete = certifications.length > 0;
      const capabilitiesComplete = capabilities.length > 0;
      const galleryComplete = Array.isArray(profileData.gallery_images) && profileData.gallery_images.length > 0;
      const verificationComplete = profileData.verification_level === 'basic' || profileData.verification_level === 'verified' || profileData.verification_level === 'premium';
      
      // Now we have 6 sections instead of 5
      const completedSections = [
        userFieldsComplete,
        companyFieldsComplete,
        certificationsComplete,
        capabilitiesComplete,
        galleryComplete,
        verificationComplete
      ].filter(Boolean).length;
      
      const overallPercentage = Math.round((completedSections / 6) * 100);

      const { data, error } = await supabase
        .from('vendor_profile_completion')
        .upsert({
          profile_id: profileId,
          basic_info_complete: basicInfoComplete,
          user_profile_complete: userFieldsComplete,
          company_profile_complete: companyFieldsComplete,
          certifications_complete: certificationsComplete,
          capabilities_complete: capabilitiesComplete,
          gallery_complete: galleryComplete,
          verification_complete: verificationComplete,
          overall_percentage: overallPercentage,
        }, {
          onConflict: 'profile_id'
        })
        .select('*')
        .single();

      if (error) throw error;
      setCompletion(data);
    } catch (err: any) {
      console.error('Failed to update completion status:', err);
    }
  };

  // Delete certification
  const deleteCertification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vendor_certifications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCertifications(prev => prev.filter(cert => cert.id !== id));
      if (profile) await updateCompletionStatus(profile.id);
      
      toast({
        title: "Success",
        description: "Certification deleted successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to delete certification",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Delete capability
  const deleteCapability = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vendor_capabilities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCapabilities(prev => prev.filter(cap => cap.id !== id));
      if (profile) await updateCompletionStatus(profile.id);
      
      toast({
        title: "Success",
        description: "Capability deleted successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to delete capability",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update organization name
  const updateOrganizationName = async (newName: string) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('update_vendor_organization_name', {
        p_user_id: user.data.user.id,
        p_new_name: newName
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Organization name updated successfully",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update organization name",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Request verification
  const requestVerification = async (level: 'basic' | 'enhanced' | 'premium') => {
    try {
      if (!profile) throw new Error('No profile found');
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // Check if already verified at this level or higher
      if (profile.is_verified) {
        const levels = { 'basic': 1, 'enhanced': 2, 'premium': 3 };
        const currentLevel = levels[profile.verification_level as keyof typeof levels] || 0;
        const requestedLevel = levels[level];
        
        if (currentLevel >= requestedLevel) {
          toast({
            title: "Already Verified",
            description: `You are already verified at ${profile.verification_level} level`,
            variant: "destructive",
          });
          return;
        }
      }

      // Check if there's already a pending request
      if (pendingRequest) {
        toast({
          title: "Request Pending",
          description: "You already have a pending verification request",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('verification_requests')
        .insert({
          vendor_profile_id: profile.id,
          requested_level: level,
          requested_by_user_id: user.data.user.id,
        })
        .select('*')
        .single();

      if (error) throw error;

      setPendingRequest(data);
      toast({
        title: "Verification Requested",
        description: `${level} verification request submitted for review`,
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to request verification",
        variant: "destructive",
      });
      throw err;
    }
  };
      

  useEffect(() => {
    fetchProfile();
    
    // Set up real-time subscription for vendor profile updates
    let cleanup: (() => void) | undefined;
    
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const channel = supabase
          .channel(`vendor-profile-${user.id}`)
          .on('postgres_changes', { 
            event: 'UPDATE', 
            schema: 'public', 
            table: 'vendor_profiles_enhanced',
            filter: `user_id=eq.${user.id}`
          }, () => {
            // Only refetch if we're not in the middle of an update
            if (!isUpdating) {
              fetchProfile();
            }
          })
          .subscribe();

        cleanup = () => {
          supabase.removeChannel(channel);
        };
      }
    };

    setupRealtimeSubscription();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Auto-recalculate completion status when relevant data changes
  // Debounce to prevent circular updates from realtime triggers
  useEffect(() => {
    if (!profile?.id) return;
    
    const timeoutId = setTimeout(() => {
      updateCompletionStatus(profile.id);
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, certifications.length, capabilities.length, profile?.gallery_images?.length, profile?.verification_level]);

  return {
    profile,
    certifications,
    capabilities,
    completion,
    pendingRequest,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    updateOrganizationName,
    addCertification,
    updateCertification,
    deleteCertification,
    addCapability,
    updateCapability,
    deleteCapability,
    updateCompletionStatus,
    requestVerification,
  };
};